import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { DEFAULT_BRAND_TOKENS } from "./tokenRegistry";

import { logout } from "@/auth/authSlice";
import {
  getBrandKit,
  resetBrandKit,
  saveBrandKit,
  type BrandKitTokens,
} from "@/services/brandKitService";

// Mirrors src/features/tenantBranding/tenantBrandingSlice.ts (same cache/TTL/logout pattern).
// Generic over whatever keys are in BRAND_TOKEN_REGISTRY — future phases don't touch this file.

const CACHE_TTL_MS = 15 * 60 * 1000;
const STORAGE_PREFIX = "brand-kit-cache:";
const LAST_TENANT_STORAGE_KEY = "brand-kit-last-tenant-id";

type BrandKitCacheEntry = {
  tokens: BrandKitTokens;
  isCustom: boolean;
  version: number;
  loadedAt: string;
  expiresAt: string;
};

type BrandKitState = {
  currentTenantId: string | null;
  applied: BrandKitTokens;
  isCustom: boolean;
  version: number;
  cache: Record<string, BrandKitCacheEntry>;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** Tenant a load most recently failed for — blocks BrandKitBootstrap's effect from
   *  re-dispatching every render (its guard only skips on a fresh cache hit, so without
   *  this a rejected load never satisfies that guard and gets retried in an unbounded
   *  render loop). Cleared on a successful load, or when a different tenant is loaded. */
  failedTenantId: string | null;
};

const initialState: BrandKitState = {
  currentTenantId: null,
  applied: { ...DEFAULT_BRAND_TOKENS },
  isCustom: false,
  version: 0,
  cache: {},
  loading: false,
  saving: false,
  error: null,
  failedTenantId: null,
};

function isFresh(expiresAt: string) {
  return Date.parse(expiresAt) > Date.now();
}

function makeCacheEntry(
  tokens: BrandKitTokens,
  isCustom: boolean,
  version: number,
): BrandKitCacheEntry {
  const loadedAt = new Date().toISOString();
  const expiresAt = new Date(Date.now() + CACHE_TTL_MS).toISOString();
  return { tokens, isCustom, version, loadedAt, expiresAt };
}

function readStorage(tenantId: string): BrandKitCacheEntry | null {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${tenantId}`);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as BrandKitCacheEntry;
  } catch {
    return null;
  }
}

function writeStorage(tenantId: string, entry: BrandKitCacheEntry) {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${tenantId}`, JSON.stringify(entry));
    // Lets index.html's pre-paint snapshot script find this tenant's cache on next load,
    // before Redux/auth exist yet. See docs/plan/brand-kit/README.md §6.
    window.localStorage.setItem(LAST_TENANT_STORAGE_KEY, tenantId);
  } catch {
    // Ignore storage failures and keep the in-memory value.
  }
}

export const loadBrandKitAsync = createAsyncThunk(
  "brandKit/load",
  async (
    { tenantId, force = false }: { tenantId: string; force?: boolean },
    { getState, rejectWithValue },
  ) => {
    const state = getState() as { brandKit: BrandKitState };
    const cached = state.brandKit.cache[tenantId];

    if (!force && cached && isFresh(cached.expiresAt)) {
      return { tenantId, entry: cached };
    }

    if (!force) {
      const stored = readStorage(tenantId);
      if (stored && isFresh(stored.expiresAt)) {
        return { tenantId, entry: stored };
      }
    }

    try {
      const brandKit = await getBrandKit();
      const entry = makeCacheEntry(brandKit.tokens, brandKit.isCustom, brandKit.version);
      writeStorage(tenantId, entry);
      return { tenantId, entry };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to load brand kit.",
      );
    }
  },
);

export const saveBrandKitAsync = createAsyncThunk(
  "brandKit/save",
  async (
    { tenantId, tokens }: { tenantId: string; tokens: BrandKitTokens },
    { rejectWithValue },
  ) => {
    try {
      const brandKit = await saveBrandKit(tokens);
      const entry = makeCacheEntry(brandKit.tokens, brandKit.isCustom, brandKit.version);
      writeStorage(tenantId, entry);
      return { tenantId, entry };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to save brand kit.",
      );
    }
  },
);

export const resetBrandKitAsync = createAsyncThunk(
  "brandKit/reset",
  async ({ tenantId }: { tenantId: string }, { rejectWithValue }) => {
    try {
      const brandKit = await resetBrandKit();
      const entry = makeCacheEntry(brandKit.tokens, brandKit.isCustom, brandKit.version);
      writeStorage(tenantId, entry);
      return { tenantId, entry };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to reset brand kit.",
      );
    }
  },
);

function applyEntry(state: BrandKitState, tenantId: string, entry: BrandKitCacheEntry) {
  state.currentTenantId = tenantId;
  state.applied = entry.tokens;
  state.isCustom = entry.isCustom;
  state.version = entry.version;
  state.cache[tenantId] = entry;
}

const brandKitSlice = createSlice({
  name: "brandKit",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadBrandKitAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadBrandKitAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.failedTenantId = null;
        applyEntry(state, action.payload.tenantId, action.payload.entry);
      })
      .addCase(loadBrandKitAsync.rejected, (state, action) => {
        state.loading = false;
        state.failedTenantId = action.meta.arg.tenantId;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : (action.error.message ?? "Failed to load brand kit.");
      })
      .addCase(saveBrandKitAsync.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveBrandKitAsync.fulfilled, (state, action) => {
        state.saving = false;
        applyEntry(state, action.payload.tenantId, action.payload.entry);
      })
      .addCase(saveBrandKitAsync.rejected, (state, action) => {
        state.saving = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : (action.error.message ?? "Failed to save brand kit.");
      })
      .addCase(resetBrandKitAsync.fulfilled, (state, action) => {
        applyEntry(state, action.payload.tenantId, action.payload.entry);
      })
      .addCase(logout, () => initialState);
  },
});

export default brandKitSlice.reducer;
